import moment from 'moment';
import store from 'Redux/store';
import { endpoints } from 'Core/defaultValues';
import { LogManagerConsole } from 'Core/logManager';

// Redux Slices Phase 1
import { upsertManyWithRules as upsertUnits } from './redux/unitSlice';
import { upsertManyWithRules as upsertLastReadings, captureDiferentialUnitsSideEffect } from './redux/lastReadingSlice';
import { upsertManyWithRules as upsertDrivers } from './redux/driverSlice';
import { upsertManyWithRules as upsertTags } from './redux/tagSlice';
import { upsertManyWithRules as upsertAssets } from './redux/assetSlice';

// Existing legacy hooks we still need to trigger
import { catalogInitialGetUnit, catalogProcessItem } from 'Redux/actions';
import { processNotification } from '../catalogs/redux/slice';
import { REMOTE_COMMAND_NOTIFICATION_TYPE } from 'Modules/.common/components/OemDataModal/remoteCommands/constants';
import { handleVehicleRemoteCommandNotification } from 'Modules/.common/components/OemDataModal/remoteCommands/remoteCommandsNotifications';

// Notification types with their own handler, kept out of the legacy catalog processing.
const LEGACY_EXCLUDED_NOTIFICATION_TYPES = ["DriverPerformance", REMOTE_COMMAND_NOTIFICATION_TYPE];

class SocketManager {
    constructor() {
        this.client = null;
        this.batchArray = [];
        this.batchInterval = null;
        this.reconnectTimeout = null;
        this.cantAttemps = 0;
        this.user = null;
        this.systemPreference = null;
        this.topics = "Unit, LastReading, Driver, Tag, Asset"; // Notifications removed for now, or added if needed
    }

    init(user, systemPreference) {
        this.user = user;
        this.systemPreference = systemPreference;

        // Start buffer interval for processing messages every 2 seconds
        if (!this.batchInterval) {
            this.batchInterval = setInterval(() => {
                const batchArrayToProcess = [...this.batchArray];
                this.batchArray = []; // clear the buffer

                if (batchArrayToProcess.length > 0) {
                    this.processTopics(batchArrayToProcess);
                }
            }, 2000);
        }

        this.createConnection();
    }

    createConnection() {
        if (this.client && this.client.readyState === this.client.OPEN) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
            LogManagerConsole("RT_DEBUG", "[SocketManager] Already connected");
            return;
        }

        const WSSURL = this.systemPreference && this.systemPreference[process.env.STACK]?.wss || endpoints.WSS_READINGS;
        const token = localStorage.getItem('IdToken');
        const wss = `${WSSURL}?token=${token}&appId=px-cloud`;

        try {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
            this.client = new WebSocket(wss);
            LogManagerConsole("RT_DEBUG", "[SocketManager] Attempting Connection...", wss.split('?')[0]);
        } catch (e) {
            LogManagerConsole("RT_DEBUG", '[SocketManager] ws error', e);
        }

        this.client.onopen = () => {
            this.cantAttemps = 0;

            let authId = "PX_" + this.user.companyId;
            if (this.user.isSubUser) authId = authId + '_' + this.user.subUserId;

            let topics = this.topics;
            let wssWithoutRedings = this.systemPreference && this.systemPreference[process.env.STACK]?.wssWithoutRedings || 0;
            if (Number(wssWithoutRedings) === 0) {
                topics = "Unit, LastReading, Driver, Tag, Asset, Notification";
            } else {
                topics = "Unit, LastReading, Driver, Tag, Asset, Notification";
            }

            LogManagerConsole("RT_DEBUG", '[SocketManager] connected socket authId:', authId, 'topics:', topics);
            this.client.send(
                JSON.stringify({
                    "action": "subscribeToTopics",
                    "topics": topics
                })
            );
        };

        this.client.onmessage = ({ data }) => {
            let messageJSON = JSON.parse(data);
            LogManagerConsole("RT_DEBUG", "[SocketManager] Received Frame Size:", Array.isArray(messageJSON) ? messageJSON.length : 1);
            if (Array.isArray(messageJSON)) {
                this.batchArray.push(...messageJSON);
            } else {
                this.batchArray.push(messageJSON);
            }
        };

        this.client.onclose = (e) => {
            LogManagerConsole("RT_DEBUG", '[SocketManager] onclose event triggered:', e.type);
            this.reconnectSocket();
        };

        this.client.onerror = (e) => {
            if (this.client.readyState === 1) {
                LogManagerConsole("RT_DEBUG", '[SocketManager] ws error after open connection', e.type);
            }
            LogManagerConsole("RT_DEBUG", '[SocketManager] ws error', e.type);
            if (this.client) this.client.close();
        };
    }

    reconnectSocket() {
        clearTimeout(this.reconnectTimeout);
        this.cantAttemps += 1;
        LogManagerConsole("RT_DEBUG", '[SocketManager] reconnectSocket attempt count:', this.cantAttemps);

        this.reconnectTimeout = setTimeout(() => {
            this.reconnectTimeout = null;
            this.createConnection();
        }, this.cantAttemps < 5 ? this.cantAttemps * 10000 : 60000);
    }

    close() {
        if (this.client) this.client.close();
        if (this.batchInterval) clearInterval(this.batchInterval);
        if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);
    }

    processTopics(dataArray) {
        // Prepare categorized bins
        const bins = {
            Unit: [],
            LastReading: [],
            Driver: [],
            Tag: [],
            Asset: [],
            Notification: []
        };

        // Classify
        dataArray.forEach(item => {
            if (bins[item.entityName]) {
                bins[item.entityName].push(item);
            }
        });

        LogManagerConsole("RT_DEBUG", "[SocketManager] Processing Batch | Units:", bins.Unit.length, "Readings:", bins.LastReading.length, "Drivers:", bins.Driver.length, "Tags:", bins.Tag.length, "Assets:", bins.Asset.length, "Notifications:", bins.Notification.length);

        // ============================================
        // 1. Process Non-Realtime / Legacy requirements
        // ============================================
        if (bins.Notification.length > 0) {
            let processNotifications = bins.Notification.filter(x => !LEGACY_EXCLUDED_NOTIFICATION_TYPES.includes(x?.payload?.type));
            processNotifications.forEach(notification => {
                store.dispatch(catalogProcessItem("Notification", notification.payload));
                store.dispatch(processNotification({ notification: notification.payload }));
            });
            bins.Notification
                .filter(x => x?.payload?.type === REMOTE_COMMAND_NOTIFICATION_TYPE)
                .forEach(notification => handleVehicleRemoteCommandNotification(notification.payload));
        }

        // ============================================
        // 2. Dispatch RTK Entity Adapter Updates
        // ============================================

        // UNITS
        if (bins.Unit.length > 0) {
            let isRefresh = bins.Unit.some((item) => item?.clientAction === "refresh");

            if (isRefresh) {
                let deleteUnitIds = bins.Unit
                    .filter(item =>
                        ((item.event == 'created' || item.event == 'updated') && item?.payload?.show == false)
                        || (item.event == 'deleted')
                    )
                    .map(item => item.entityId) || [];

                let updateUnits = bins.Unit.filter(item => !deleteUnitIds.includes(item.entityId));
                let filteredUnitItems = updateUnits.filter(item => (item?.event == 'created' || item?.event == 'updated'));

                if (filteredUnitItems.length > 0) {
                    store.dispatch(catalogInitialGetUnit({ ids: filteredUnitItems.map(e => e?.entityId) }));
                }

                // Push explicitly formatted units to RTK Adapter
                let formattedUnits = filteredUnitItems.map(item => ({ ...item.payload, id: item.entityId, event: item.event }));
                if (formattedUnits.length > 0) store.dispatch(upsertUnits(formattedUnits));

                // For deletions: could dispatch removeUnits(deleteUnitIds) here if we implemented it
                // store.dispatch(removeUnits(deleteUnitIds));
            } else {
                let formattedUnits = bins.Unit.map(item => {
                    let event = item?.event;
                    if (item?.payload?.show === false) event = 'deleted';
                    return { ...item.payload, id: item.entityId, event };
                });
                store.dispatch(upsertUnits(formattedUnits));
            }
        }

        // LAST READING
        if (bins.LastReading.length > 0) {
            sessionStorage.setItem("lastSyncLastReading", moment());
            let formattedReadings = bins.LastReading.map(item => ({ ...item.payload, id: item.entityId, event: item.event }));
            store.dispatch(upsertLastReadings(formattedReadings));

            // Fire legacy side effect manually
            captureDiferentialUnitsSideEffect(formattedReadings);
        }

        // DRIVERS
        if (bins.Driver.length > 0) {
            let formattedDrivers = bins.Driver.map(item => ({ ...item.payload, id: item.entityId, event: item.event }));
            store.dispatch(upsertDrivers(formattedDrivers));
        }

        // TAGS
        if (bins.Tag.length > 0) {
            let formattedTags = bins.Tag.map(item => ({ ...item.payload, id: item.entityId, event: item.event }));
            store.dispatch(upsertTags(formattedTags));
        }

        // ASSETS
        if (bins.Asset.length > 0) {
            let formattedAssets = bins.Asset.map(item => ({ ...item.payload, id: item.entityId, event: item.event }));
            store.dispatch(upsertAssets(formattedAssets));
        }
    }
}

// Singleton Pattern for global access
const instance = new SocketManager();
export default instance;
